/**
 * Admin doctor directory: approve, suspend, reactivate.
 *
 * Replaces `renderAdminList`, `toggleSuspend`, `deleteDoctor`, `openEditModal`,
 * `handleEditDoctor` and `switchAdminTab`'s doctors branch.
 *
 * Note: the legacy UI offered a "delete doctor" button, but the backend exposes
 * no delete route for doctors — the button silently did nothing beyond a local
 * splice that the next refresh undid. Suspension is the real capability, so that
 * is what this screen offers.
 */

import { useMemo, useState } from 'react';
import {
  Avatar,
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  PageHeader,
  Panel,
  Select,
  Spinner,
  StatCard,
} from '../../components/common';
import { useAllDoctors, useData } from '../../context/DataContext';
import { AddDoctorForm } from './AddDoctorForm';
import { useNotifications } from '../../context/NotificationContext';
import { useDebounced } from '../../hooks/useLocalStorage';
import { adminApi } from '../../api/endpoints';
import { normalizeDoctors, categoryName } from '../../domain/catalog';
import { doctorPriceRange, formatPriceRange, BOOKING_TYPES } from '../../domain/pricing';

const STATUS_FILTERS = [
  { value: '', label: 'كل الحالات' },
  { value: 'active', label: 'نشط' },
  { value: 'pending', label: 'قيد المراجعة' },
  { value: 'suspended', label: 'موقوف' },
];

/**
 * The team member assigned to a doctor.
 *
 * The legacy table let an admin assign one from this cell. That is a separate
 * screen now (إدارة الفريق), so the column reports the assignment rather than
 * offering to change it — a button that opened nothing would be worse than a
 * label.
 */
const moderatorFor = (doctor, teamMembers) => {
  const id = doctor.moderatorId ?? doctor.ModeratorId;
  if (!id) return null;
  return (teamMembers || []).find((member) => String(member.id) === String(id)) || null;
};

const STATUS_META = {
  active: { label: 'نشط', className: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' },
  pending: { label: 'قيد المراجعة', className: 'border-amber-500/30 bg-amber-500/10 text-amber-300' },
  suspended: { label: 'موقوف', className: 'border-red-500/30 bg-red-500/10 text-red-300' },
};

export function AdminDoctors() {
  const { data, loading, error, reload } = useAllDoctors();
  const { teamMembers } = useData();
  const { success, error: toastError } = useNotifications();

  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [pendingId, setPendingId] = useState(null);
  const [suspendTarget, setSuspendTarget] = useState(null);

  const debouncedQuery = useDebounced(query, 250);
  const doctors = useMemo(() => normalizeDoctors(data || []), [data]);

  const counts = useMemo(
    () => ({
      total: doctors.length,
      active: doctors.filter((d) => d.status === 'active').length,
      pending: doctors.filter((d) => d.status === 'pending').length,
      suspended: doctors.filter((d) => d.status === 'suspended').length,
    }),
    [doctors],
  );

  const filtered = useMemo(() => {
    const needle = debouncedQuery.trim().toLowerCase();
    return doctors.filter((doctor) => {
      if (status && doctor.status !== status) return false;
      if (!needle) return true;
      return (
        String(doctor.name).toLowerCase().includes(needle) ||
        String(doctor.username).toLowerCase().includes(needle) ||
        String(doctor.categoryName).toLowerCase().includes(needle)
      );
    });
  }, [doctors, status, debouncedQuery]);

  const run = async (id, action, message) => {
    setPendingId(id);
    try {
      await action();
      success(message);
      reload();
    } catch (err) {
      toastError(err.message || 'تعذر تنفيذ العملية');
    } finally {
      setPendingId(null);
      setSuspendTarget(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="الأطباء"
        subtitle={`${counts.total} طبيب مسجل`}
        icon="stethoscope"
        actions={
          <Button variant="ghost" icon="refresh-cw" onClick={reload} loading={loading}>
            تحديث
          </Button>
        }
      />

      <AddDoctorForm onCreated={reload} />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon="users" label="إجمالي الأطباء" value={counts.total} accent="cyan" />
        <StatCard icon="check-circle-2" label="نشط" value={counts.active} accent="emerald" />
        <StatCard icon="clock" label="قيد المراجعة" value={counts.pending} accent="amber" />
        <StatCard icon="ban" label="موقوف" value={counts.suspended} accent="red" />
      </div>

      <Panel>
        <div className="flex flex-wrap gap-3">
          <div className="relative min-w-[220px] flex-1">
            <Icon
              name="search"
              size={16}
              className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
            />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="ابحث بالاسم أو اسم المستخدم أو التخصص..."
              className="pr-9"
            />
          </div>

          <Select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            className="sm:w-48"
          >
            {STATUS_FILTERS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>
      </Panel>

      {error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : loading && doctors.length === 0 ? (
        <Spinner label="جاري تحميل الأطباء..." />
      ) : filtered.length === 0 ? (
        <Panel>
          <EmptyState icon="user-x" title="لا يوجد أطباء مطابقون" />
        </Panel>
      ) : (
        <Panel>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h3 className="flex items-center gap-2 text-lg font-bold text-white">
              <Icon name="users" size={20} className="text-cyan-400" />
              الأطباء المسجلين
            </h3>
            <div className="text-xs text-slate-400">
              <span className="text-emerald-400">{counts.active}</span> نشط{' '}
              <span className="text-slate-600">|</span>{' '}
              <span className="text-red-400">{counts.suspended}</span> معطل
            </div>
          </div>

          {/* The table scrolls inside its own panel. Seven columns do not fit a
              phone, and letting the page scroll sideways instead would push the
              sidebar off-screen. */}
          <div className="custom-scrollbar -mx-2 overflow-x-auto px-2">
            <table className="w-full min-w-[860px] text-right">
              <thead className="border-b border-white/10 text-xs text-slate-400">
                <tr>
                  <th className="w-1/4 pb-4 font-medium">اللقب</th>
                  <th className="w-1/6 pb-4 font-medium">التخصص</th>
                  <th className="w-[200px] pb-4 text-center font-medium">موديريتور</th>
                  <th className="w-[90px] pb-4 font-medium text-emerald-400">الكشف</th>
                  <th className="w-[90px] pb-4 font-medium text-cyan-400">المتابعة</th>
                  <th className="pb-4 font-medium">الحالة</th>
                  <th className="pb-4 font-medium">إجراءات</th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((doctor) => {
                  const meta = STATUS_META[doctor.status] || STATUS_META.pending;
                  const busy = String(pendingId) === String(doctor.id);
                  const moderator = moderatorFor(doctor, teamMembers);
                  const suspended = doctor.status === 'suspended';

                  return (
                    <tr
                      key={doctor.id}
                      className={`border-b border-white/5 transition-colors hover:bg-white/5 ${
                        suspended ? 'opacity-50' : ''
                      }`}
                    >
                      <td className="py-3 pl-4 font-medium">
                        <div className="flex items-center gap-3">
                          <Avatar src={doctor.image} name={doctor.name} size={40} />
                          <div className="min-w-0">
                            <div className="truncate font-bold text-white">{doctor.name}</div>
                            <div className="truncate text-xs text-slate-400">
                              {doctor.title || '—'}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="py-3 text-xs text-slate-400">
                        {categoryName(doctor.category)}
                      </td>

                      <td className="px-4 py-3 text-center">
                        {moderator ? (
                          <span className="mx-auto flex w-full max-w-[190px] items-center gap-2 rounded-lg border border-cyan-500/20 bg-cyan-500/10 p-2.5 text-cyan-300">
                            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-cyan-500 text-[10px] font-bold text-white">
                              {moderator.name?.charAt(0) || '?'}
                            </span>
                            <span className="truncate text-sm font-bold">{moderator.name}</span>
                          </span>
                        ) : (
                          <span className="mx-auto flex w-full max-w-[190px] items-center justify-center gap-2 rounded-lg border border-dashed border-white/10 bg-slate-800/60 p-2.5 text-sm text-slate-500">
                            غير معيّن
                          </span>
                        )}
                      </td>

                      <td className="py-3 font-mono text-sm text-emerald-400">
                        {formatPriceRange(
                          doctorPriceRange({ doctor, bookingType: BOOKING_TYPES.EXAM }),
                        )}
                      </td>

                      <td className="py-3 font-mono text-sm text-cyan-400">
                        {formatPriceRange(
                          doctorPriceRange({ doctor, bookingType: BOOKING_TYPES.FOLLOWUP }),
                        )}
                      </td>

                      <td className="py-3">
                        <Badge className={meta.className}>{meta.label}</Badge>
                      </td>

                      <td className="py-3">
                        <div className="flex items-center gap-1">
                          {doctor.status === 'active' ? (
                            <button
                              type="button"
                              onClick={() => setSuspendTarget(doctor)}
                              title="تعطيل"
                              aria-label={`تعطيل ${doctor.name}`}
                              className="rounded-lg p-2 text-orange-400 transition-colors hover:bg-orange-500/20"
                            >
                              <Icon name="pause" size={16} />
                            </button>
                          ) : (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() =>
                                run(
                                  doctor.id,
                                  () => adminApi.approveDoctor(doctor.id),
                                  suspended ? 'تم إعادة التفعيل' : 'تم تفعيل الطبيب',
                                )
                              }
                              title="تفعيل"
                              aria-label={`تفعيل ${doctor.name}`}
                              className="rounded-lg p-2 text-emerald-400 transition-colors hover:bg-emerald-500/20 disabled:opacity-50"
                            >
                              <Icon name={busy ? 'loader-2' : 'play'} size={16} className={busy ? 'animate-spin' : ''} />
                            </button>
                          )}

                          <span className="text-[11px] text-slate-500" dir="ltr">
                            @{doctor.username}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      <ConfirmDialog
        open={Boolean(suspendTarget)}
        onClose={() => setSuspendTarget(null)}
        onConfirm={() =>
          run(
            suspendTarget.id,
            () => adminApi.suspendDoctor(suspendTarget.id),
            'تم إيقاف حساب الطبيب',
          )
        }
        loading={Boolean(pendingId)}
        destructive
        title="إيقاف حساب الطبيب"
        confirmLabel="إيقاف الحساب"
        message={`سيتم منع ${suspendTarget?.name || 'الطبيب'} من تسجيل الدخول، وستختفي صفحته من قائمة الأطباء المتاحين للحجز.\n\nالحجوزات القائمة لن تُحذف.`}
      />
    </div>
  );
}

export default AdminDoctors;
